import {
  SubItems,
  SubItem,
} from '@engyalo/delivery-ui-components/lib/interfaces/catalog'
import { IAddCartProductArgs, TBundle } from '../../interfaces'
import { isSubItem } from '../../utils/isSubItem'
import addPromotionalQtyToValue from './addPromotionalQtyToValue'

export interface ICalculateAddFinalValue
  extends Pick<
    IAddCartProductArgs,
    | 'value'
    | 'newValue'
    | 'isBundle'
    | 'newSubItems'
    | 'currentSubItems'
    | 'currentRoute'
    | 'resultFixed'
    | 'isPackage'
    | 'isFromCounterButtons'
    | 'capSource'
    | 'isClampedToCap'
  > {
  subItemEdit?: SubItem | null
  name: string
  packageNameFromCart: string | null
  minQtyAllowed?: number
  autoAdd: boolean
  freegoodsAsSubitem: boolean
  promotionalQty: number
}

export interface ICalculateAddFinalValueResult {
  handledValue: number
  cleanedSubItems: Array<{
    quantity: number
    items: Record<string, number>
  }>
  replaceCart: boolean
  packageName: string | null
  showAlert: boolean
}

type ValidationsCondition = (
  isFromCatalog: boolean,
  isBundle: boolean,
  hasEditSubItem: boolean,
  subItem: SubItem | TBundle | undefined,
) => boolean

type ValidationAction = (
  currentSubItems: SubItems,
  newSubItems: SubItem | TBundle,
  newValue: number,
  previousSubItem?: SubItem | null,
) => SubItems

type ValidateActions = [
  condition: ValidationsCondition,
  action: ValidationAction,
][]

const isCatalogSubItem = (
  isFromCatalog: boolean,
  isBundle: boolean,
  hasEditSubItem: boolean,
  subItem: SubItem | TBundle | undefined,
) => isFromCatalog && isBundle && !hasEditSubItem && !isSubItem(subItem)

const isCartSubItem = (
  isFromCatalog: boolean,
  isBundle: boolean,
  hasEditSubItem: boolean,
  subItem: SubItem | TBundle | undefined,
) => !isFromCatalog && isBundle && !hasEditSubItem && isSubItem(subItem)

const isEditSubItem = (
  isFromCatalog: boolean,
  isBundle: boolean,
  hasEditSubItem: boolean,
  subItem: SubItem | TBundle | undefined,
) => !isFromCatalog && isBundle && hasEditSubItem && !isSubItem(subItem)

const handleCatalogSubItem = (
  currentSubItems: SubItems,
  newSubItems: SubItem | TBundle,
  newValue: number,
) => {
  if (isSubItem(newSubItems)) {
    return []
  }
  const handledSubItems = currentSubItems || []
  handledSubItems.push({
    items: {
      ...newSubItems,
    },
    quantity: newValue,
  })
  return handledSubItems
}

const handleCartSubItem = (
  currentSubItems: SubItems,
  newSubItems: SubItem | TBundle,
  newValue: number,
) => {
  if (!isSubItem(newSubItems)) {
    return []
  }
  const handledSubItems = currentSubItems || []
  const hasPreviousSubItems = !!handledSubItems.length
  const indexOfBundle = hasPreviousSubItems
    ? handledSubItems?.findIndex((item) => {
        return item.hash === newSubItems.hash
      })
    : 0
  handledSubItems[indexOfBundle] = {
    ...newSubItems,
    items: {
      ...newSubItems.items,
    },
    quantity: newValue,
  }
  return handledSubItems
}

const handleEditSubItem = (
  currentSubItems: SubItems,
  newSubItems: SubItem | TBundle,
  newValue: number,
  previousSubItem?: SubItem | null,
) => {
  if (!previousSubItem) {
    return []
  }
  if (isSubItem(newSubItems)) {
    return []
  }
  const handledSubItems = currentSubItems || []
  const indexOfBundle = handledSubItems?.findIndex((item) => {
    return item.hash === previousSubItem.hash
  })
  handledSubItems[indexOfBundle] = {
    ...previousSubItem,
    items: {
      ...newSubItems,
    },
    quantity: newValue,
  }
  return handledSubItems
}

/**
 * Whether the caller should raise the "only N left in stock" alert.
 *
 * `resultFixed` was never a reliable proxy for it. Both of its producers predate
 * this change — the counter has composed it as `isClampedToCap || allowSetMaxvalue`
 * pin since before COM-653, and the pin resolves to `Math.max(stock, max)`, so
 * neither producer guaranteed the number was stock. What COM-653 changed is that
 * `maxQty` joined the cap, which made an already-possible mismatch common: the
 * effective cap may now be the product's purchase ceiling, a configured `max`, or
 * the list's remaining-items limit — none of which are stock, while the alert
 * hardcodes the product's stock as its number.
 *
 * So the clamp is only reported as a stock problem when stock is what produced the
 * cap. Every other clamp is reported by the cart's own ceiling alert instead, and
 * adding this one on top tells the customer the wrong constraint with the wrong
 * figure. Note that ceiling alert is worded per storefront: with none configured
 * the cart falls back to its generic adjustment message, so suppressing this one
 * never leaves a real clamp unannounced.
 *
 * Both fields arrive through config action args and may be unresolved template
 * strings on a counter that predates them; neither then matches, which leaves the
 * original behavior in place.
 */
export const shouldShowStockAlert = ({
  resultFixed,
  isClampedToCap,
  capSource,
}: Pick<
  ICalculateAddFinalValue,
  'resultFixed' | 'isClampedToCap' | 'capSource'
>): boolean => {
  if (!resultFixed) return false
  const clampedToNonStockCap = isClampedToCap === true && capSource !== 'stock'
  return !clampedToNonStockCap
}

const validations: ValidateActions = [
  [isCatalogSubItem, handleCatalogSubItem],
  [isCartSubItem, handleCartSubItem],
  [isEditSubItem, handleEditSubItem],
]

export const calculateAddFinalValueProduct = (
  args: ICalculateAddFinalValue,
): ICalculateAddFinalValueResult => {
  const {
    newValue,
    minQtyAllowed = 0,
    autoAdd,
    freegoodsAsSubitem,
    promotionalQty,
    isFromCounterButtons,
  } = args
  const valueWithPromotionalQty = addPromotionalQtyToValue({
    valueToUpdate: newValue,
    promotionalQty,
    freegoodsAsSubitem,
    autoAdd,
  })
  const finalValue = isFromCounterButtons ? valueWithPromotionalQty : newValue
  return {
    showAlert: shouldShowStockAlert(args),
    handledValue: finalValue - minQtyAllowed,
    cleanedSubItems: [],
    replaceCart: true,
    packageName: null,
  }
}

export const calculateAddFinalValueSubitems = (
  args: ICalculateAddFinalValue,
): ICalculateAddFinalValueResult => {
  const {
    currentRoute,
    subItemEdit,
    isBundle,
    newSubItems,
    currentSubItems,
    newValue,
  } = args
  const isFromCatalog = currentRoute === 'catalog' && !subItemEdit
  const hasEditSubItem = !!subItemEdit && isSubItem(subItemEdit)
  const replaceCart = !isFromCatalog
  let handledSubItems: SubItems = currentSubItems || []
  const validation = validations.find(([condition]) =>
    condition(isFromCatalog, isBundle || false, hasEditSubItem, newSubItems),
  )
  if (validation) {
    const [, action] = validation
    handledSubItems = action(
      handledSubItems,
      newSubItems || {},
      newValue,
      subItemEdit,
    )
  }
  const handledValue = handledSubItems.reduce(
    (acc, item) => acc + item.quantity,
    0,
  )
  const cleanedSubItems = handledSubItems?.map(
    ({ hash, ...subItemValue }) => subItemValue,
  )
  return {
    showAlert: shouldShowStockAlert(args),
    handledValue,
    cleanedSubItems,
    replaceCart,
    packageName: null,
  }
}
export const calculateAddFinalValuePackage = (
  args: ICalculateAddFinalValue,
): ICalculateAddFinalValueResult => {
  const {
    newValue,
    minQtyAllowed = 0,
    isPackage,
    packageNameFromCart,
    name,
  } = args
  return {
    showAlert: shouldShowStockAlert(args),
    handledValue: newValue - minQtyAllowed,
    cleanedSubItems: [],
    replaceCart: true,
    packageName: isPackage ? name : packageNameFromCart,
  }
}

export const calculateAddFinalValue = (
  args: ICalculateAddFinalValue,
): ICalculateAddFinalValueResult | undefined => {
  const {
    value,
    newValue,
    isBundle,
    resultFixed,
    isPackage,
    packageNameFromCart,
  } = args
  if (value !== newValue || isBundle || resultFixed) {
    if (!isBundle && !isPackage && !packageNameFromCart) {
      return calculateAddFinalValueProduct(args)
    }
    if (isPackage || !!packageNameFromCart) {
      return calculateAddFinalValuePackage(args)
    }
    if (isBundle) {
      return calculateAddFinalValueSubitems(args)
    }
  }
}
